import axios from 'axios';

/**
 * Utility class for handling chunked uploads of large files
 */
class ChunkedUploader {
  /**
   * Initialize the chunked uploader
   * @param {Object} options Configuration options
   * @param {string} options.baseUrl API base URL
   * @param {string} options.token Authentication token
   * @param {string} options.user User identifier
   * @param {number} options.chunkSize Size of each chunk in bytes (default: 5MB)
   * @param {Function} options.onProgress Progress callback function
   */
  constructor(options) {
    this.baseUrl = options.baseUrl;
    this.token = options.token;
    this.user = options.user;
    this.chunkSize = 2 * 1024 * 1024; // Default to 2MB chunks if not specified
    this.onProgress = options.onProgress || (() => {});
  }

  /**
   * Upload a file in chunks
   * @param {File} file The file to upload
   * @returns {Promise<Object>} Promise resolving to the upload result
   */
  async uploadFile(file) {
    try {
      // Step 1: Initialize the chunked upload
      const initResponse = await axios.post(
        `${this.baseUrl}/init-chunked-upload`,
        {
          filename: file.name
        },
        {
          headers: {
            'Token': `Bearer ${this.token}`,
            'User': this.user,
            'Content-Type': 'application/json'
          }
        }
      );

      if (initResponse.status !== 200) {
        throw new Error('Failed to initialize chunked upload');
      }

      
      // Step 2: Split the file into chunks and upload them
      const totalChunks = Math.ceil(file.size / this.chunkSize);
      
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * this.chunkSize;
        const end = Math.min(file.size, start + this.chunkSize);
        const chunk = file.slice(start, end);
        
        // Convert chunk to base64
        const base64Chunk = await this.fileChunkToBase64(chunk);
        
        // log the size of the chunk in MB
        console.log('Chunk size:', base64Chunk.length / (1024 * 1024));
        
        // Upload the chunk
        const chunkResponse = await axios.post(
          `${this.baseUrl}/upload-chunk`,
          {
            upload_id: initResponse.data.upload_id,
            chunk_index: chunkIndex,
            total_chunks: totalChunks,
            chunk_data: base64Chunk,
            filename: file.name
          },
          {
            headers: {
              'Token': `Bearer ${this.token}`,
              'User': this.user,
              'Content-Type': 'application/json'
            }
          }
        );
        
        // Update progress - allocate 90% of progress to chunk uploads
        const progress = ((chunkIndex + 1) / totalChunks) * 90;
        this.onProgress(progress);
      }
      
      // Step 3: Finalize the upload after all chunks are uploaded
      this.onProgress(95); // Show that we're finalizing
      
      const finalizeResponse = await axios.post(
        `${this.baseUrl}/finalize-upload`,
        {
          upload_id: initResponse.data.upload_id,
          total_chunks: totalChunks,
          filename: file.name
        },
        {
          headers: {
            'Token': `Bearer ${this.token}`,
            'User': this.user,
            'Content-Type': 'application/json'
          }
        }
      );
      
      this.onProgress(100);
      return finalizeResponse.data;
      
    } catch (error) {
      console.error('Chunked upload error:', error);
      throw error;
    }
  }

  /**
   * Convert a file chunk to base64
   * @param {Blob} chunk The file chunk
   * @returns {Promise<string>} Promise resolving to base64 string
   */
  fileChunkToBase64(chunk) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result.split(',')[1]; // Remove the data URL prefix
        resolve(base64String);
      };
      reader.onerror = (e) => {
        reject(e);
      };
      reader.readAsDataURL(chunk);
    });
  }
}

export default ChunkedUploader; 